import { toRaw, unref } from 'vue'
import L from 'leaflet'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { canvasIconLayer } from '@map-sdk/sdk/plugins/canvasMarker'
import { drawUtil } from '@map-sdk/sdk/plugins/Draw/DrawUtils'

interface TrackDrawSymbolImplOptions {
  callBack?: (...params: any) => void
  drawEndCallBack?: (...params: any) => void
  unit?: string
  symbol?: string
  lineColor?: string
  lineTextColor?: string
  dash?: boolean
  dashArray?: number[]
  showMarkerText?: boolean
  showLineText?: boolean
}

// 定义 TrackDrawSymbolImpl 类
class TrackDrawSymbolImpl {
  options: TrackDrawSymbolImplOptions
  id: string
  map: any // 应替换为实际的地图对象类型
  offsetC: any // 应根据实际类型定义
  items: any[] // 应根据实际类型定义
  markers: any[] // 同上
  txtMarkers: any[]
  polyLineLayer!: any // 应根据实际的PolyLine Layer类型定义
  polyLineShape: any
  featureGroup: any // 应根据实际的图层组类型定义

  constructor(map: any, options?: TrackDrawSymbolImplOptions, trackData?: any) {
    this.map = map
    this.id = `${Date.now()}_tds`
    this.options = {
      unit: 'nm',
      symbol: '',
      lineColor: '#DA462D',
      lineTextColor: '#DA462D',
      dash: true,
      dashArray: [5, 5],
      showMarkerText: true,
      showLineText: true,
      ...(options || {}),
    }

    this.offsetC = this.map.offsetC
    this.items = []
    this.markers = []
    this.txtMarkers = []
    this.polyLineShape = null

    this.featureGroup = L.featureGroup().addTo(this.map)
    if (!this.map.markersLayer_track) {
      this.map.markersLayer_track = canvasIconLayer()
    }
    this.map.markersLayer_track.addTo(this.map)
    if (!this.map.canvas_track) {
      this.map.canvas_track = L.canvas()
    }
    if (trackData) {
      this.showTrack(trackData, true, false)
    }
    if (this.map.shipsService) {
      this.map.shipsService.setZIndex()
    }
    this.map.off('moveend', this._Event.moveend, this)
    this.map.on('moveend', this._Event.moveend, this)
    this.map.off('baselayerchange', this._Event.baselayerchange, this)
    this.map.on('baselayerchange', this._Event.baselayerchange, this)
  }

  private _Event = {
    moveend: () => this._onMapZoom,
    baselayerchange: () => this._onBaseLayerChange,
  }
  private _onMapZoom(): void {
    this._drawAllMarker()
    this._drawText()
  }
  private _onBaseLayerChange(): void {
    let markerLatLngs: L.LatLngLiteral[] = []
    if (this.markers && this.markers.length > 0) {
      markerLatLngs = this._getMarkerLatLngs()
    } else if (this.polyLineLayer) {
      markerLatLngs = this.polyLineLayer.getLatLngs()
    }

    if (
      markerLatLngs &&
      markerLatLngs.length > 0 &&
      this.offsetC !== this.map.offsetC
    ) {
      if (this.map.offsetC) {
        for (const markerLatLng of markerLatLngs) {
          let latlng = L.latLng(markerLatLng.lat, markerLatLng.lng)
          latlng = CanvasShipUtils.GPSEncryptByMapToLatLng(latlng, this.map)
          markerLatLng.lat = latlng.lat
          markerLatLng.lng = latlng.lng
        }
      } else {
        for (const markerLatLng of markerLatLngs) {
          let latlng = L.latLng(markerLatLng.lat, markerLatLng.lng)
          latlng = CanvasShipUtils.getGPSLatLngByMap(latlng, this.map)
          markerLatLng.lat = latlng.lat
          markerLatLng.lng = latlng.lng
        }
      }
    }
    this.setItemsByArray(markerLatLngs)
    this.offsetC = this.map.offsetC
  }

  private showTrack(
    trackData: any,
    fitBounds: boolean,
    drawImmediately = false,
    offsetLng?: number
  ): void {
    trackData && this.setItems(trackData)
    if (offsetLng != undefined) {
      for (let i = 0; i < this.items.length; i++) {
        this.items[i].lng += Number(offsetLng)
      }
    }
    this.removeTrack(false, drawImmediately)
    this._drawAllMarker()
    this._drawText()
    this._drawLine()
    if (fitBounds) this._fitBounds()
    setTimeout(() => {
      if (this.map.markersLayer_track) {
        this.map.markersLayer_track._reset()
      }
    }, 10)
  }

  private removeTrack(clearAll = false, endDraw = false): void {
    if (endDraw) {
      this.endDrawTrack()
    }
    this._removeAllMarker()
    if (this.featureGroup) {
      this.featureGroup.clearLayers()
      this.featureGroup.remove()
      this.featureGroup = null
    }
    this._removeAllTxtMarker()
    this._removeAllLine()
    if (clearAll) {
      this.items = []
    }
  }

  private _getLatLngArray(): L.LatLngExpression[] {
    const latLngArray: L.LatLngExpression[] = []
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i]
      latLngArray.push([item.lat, item.lng])
    }
    return latLngArray
  }

  private _getLatLngs(): L.LatLng[] {
    const latLngs: L.LatLng[] = []
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i]
      latLngs.push(L.latLng([item.lat, item.lng]))
    }
    return latLngs
  }

  private _getMarkerLatLngs(): L.LatLng[] {
    const latLngs: L.LatLng[] = []
    for (let i = 0; i < this.markers.length; i++) {
      const marker = this.markers[i]
      latLngs.push(marker.getLatLng())
    }
    return latLngs
  }

  private _fitBounds(): void {
    const map = toRaw(unref(this.map))
    const latLngArray = this._getLatLngArray()
    if (latLngArray && latLngArray.length > 0) {
      map.fitBounds(CanvasShipUtils.getLatLngBoundsBoxArea(latLngArray))
      map.fire('moveend')
    }
  }

  private setItems(items: any[]): void {
    const map = toRaw(unref(this.map))
    this.removeTrack(true)
    const processedItems = CanvasShipUtils.latlngsFrom180(items)
    this.items = processedItems
    if (map.offsetC) {
      for (let i = 0; i < this.items.length; i++) {
        const item = this.items[i]
        const latlng = CanvasShipUtils.GPSEncryptByMapToLatLng(
          L.latLng(item.lat, item.lng),
          map
        )
        this.items[i].lat = latlng.lat
        this.items[i].lng = latlng.lng
      }
    }
    this.calculationTrack()
  }

  private setItemsByArray(coords: L.LatLngLiteral[]): void {
    this.items = []
    for (const [i, coord] of coords.entries()) {
      this.items.push({
        id: i,
        lat: coord.lat,
        lng: coord.lng,
      })
    }
    this.calculationTrack()
    this._drawAllMarker()
    this._drawText()
    this._drawLine()
  }

  /**
   * 计算距离
   */
  private calculationTrack(): void {
    if (this.items) {
      let totalDistance = 0
      this.items.forEach((item, index) => {
        item.id = index + 1
        item.name = this.options.symbol
          ? this.options.symbol + item.id
          : `${item.id}`
        item.bDistance = 0
        item.pDistance = 0
        item.eDistance = 0
        const latlng = L.latLng(item.lat, item.lng)
        if (index > 0) {
          const prevLatlng = L.latLng(
            this.items[index - 1].lat,
            this.items[index - 1].lng
          )
          item.pDistance = CanvasShipUtils.getDistance(
            [item.lat, item.lng],
            [this.items[index - 1].lat, this.items[index - 1].lng],
            this.options.unit || 'nm'
          )
          totalDistance += Number(item.pDistance)
          item.bDistance = totalDistance.toFixed(2)
          item.eDistance = 0
          item.cog = CanvasShipUtils.getAngleByLatLngToBigCircle(
            prevLatlng,
            latlng
          )
        }
      })
      let endDistance = 0
      for (let i = this.items.length - 2; i >= 0; i--) {
        endDistance += Number(this.items[i + 1].pDistance)
        this.items[i].eDistance = endDistance.toFixed(2)
      }
      if (this.options.callBack) {
        let llx = 0
        const adjustedItems = this.items.map((item) => {
          const adjustedItem = { ...item }
          if (adjustedItem.lng >= 180 && llx !== 360) {
            llx = 360
          }
          if (this.map.offsetC) {
            const adjustedLatlng = CanvasShipUtils.getGPSLatLngByMap(
              L.latLng(adjustedItem.lat, adjustedItem.lng),
              this.map
            )
            adjustedItem.lat = adjustedLatlng.lat
            adjustedItem.lng = adjustedLatlng.lng
          }
          return adjustedItem
        })
        this.options.callBack(CanvasShipUtils.latlngsTo180(adjustedItems), llx)
      }
    }
  }

  public getItems(): any[] {
    return this.items
  }
  public drawTrack(): void {
    if (this.map.shipsService) {
      this.map.shipsService.setZIndex()
    }
    this.showTrack(null, false, true)
    setTimeout(() => {
      this.map._container.classList.remove('m_cursor')
      this.map._container.classList.add('m_cursor')
      this.map.doubleClickZoom.disable()
      this.map.off('click', this._onClick, this)
      this.map.off('dblclick', this._onDblclick, this)
      this.map.on('click', this._onClick, this)
      this.map.on('dblclick', this._onDblclick, this)
    }, 100)
  }

  private _drawText(): void {
    let showText = false
    let latLngs: L.LatLng[] | L.LatLng[][] | L.LatLng[][][] = []
    if (this.markers && this.markers.length > 0) {
      latLngs = this._getLatLngs()
    } else if (this.polyLineLayer) {
      latLngs = this.polyLineLayer.getLatLngs()
    }

    const bounds = this.map.getBounds()
    for (const latLng of latLngs) {
      if (bounds.contains(latLng)) {
        showText = true
        break
      }
    }
    if (showText) {
      this._drawLineText()
    }
  }

  private _onClick(e: L.LeafletMouseEvent): void {
    this.addMarker(e.latlng)
  }

  private _onDblclick(): void {
    if (this.items.length > 0) {
      this.items.splice(-1, 1)
      this.showTrack(null, false, true)
      this.calculationTrack()
    }
  }
  public addMarker(latlng: L.LatLng): void {
    const map = toRaw(unref(this.map))
    this.items.push(latlng)
    this.calculationTrack()
    const marker = this._addOneMarker(this.items.length - 1)
    const showText = this._markerTextShow(
      false,
      false,
      marker,
      this.items[this.items.length - 1].name
    )
    if (!this.featureGroup) {
      this.featureGroup = L.featureGroup().addTo(map)
    }
    marker.addTo(map.markersLayer_track)
    if (showText) {
      marker.openTooltip()
    }
    this._drawLine()
    this._drawText()
  }

  private _addOneMarker(index: number): L.Marker {
    const icon = L.icon({
      iconUrl: ShipxyOptions.COM_IMG_BASE64.circular_01,
      iconSize: [8, 8],
      iconAnchor: [4, 4],
    })
    const item = this.items[index]
    const latlng = L.latLng(item.lat, item.lng)
    const marker = L.marker(latlng, { icon })
    marker.off('click')
    this.markers.push(marker)
    return marker
  }

  private _getMarkerTextShowFlg(marker1: L.Marker, marker2: L.Marker): boolean {
    return !(
      CanvasShipUtils.getMaxXYByLatlngs(
        this.map,
        marker1.getLatLng(),
        marker2.getLatLng()
      ) < 50
    )
  }

  private _markerTextShow(
    isShow: boolean,
    zoomLevel: boolean,
    marker: L.Marker,
    text: string,
    referenceElement?: L.Marker
  ): boolean {
    const zoom = this.map.getZoom()
    if (!zoomLevel && referenceElement) {
      zoomLevel =
        zoom > 8 &&
        isShow &&
        this._getMarkerTextShowFlg(referenceElement, marker)
    }
    // TODO: 将会影响缩放事件
    marker.bindTooltip(text, {
      direction: 'auto',
      offset: new L.Point(0, -15),
      opacity: 1,
      permanent: zoomLevel,
      className: 'wp_marker',
    })

    return false //zoomLevel
  }

  private _drawLineText(): void {
    this._removeAllTxtMarker()
    if (this.options.showLineText && this.items && this.items.length > 0) {
      const lineTextColor = this.options.lineTextColor || '#DA462D'
      const unit = this.options.unit || 'nm'
      if (!this.featureGroup) {
        this.featureGroup = L.featureGroup().addTo(this.map)
      }
      for (let i = 0; i < this.items.length; i++) {
        if (i > 0) {
          const currentLatLng = L.latLng(this.items[i].lat, this.items[i].lng)
          const previousLatLng = L.latLng(
            this.items[i - 1].lat,
            this.items[i - 1].lng
          )
          const latlngs = [previousLatLng, currentLatLng]
          if (
            CanvasShipUtils.getMaxXYByLatlngs(
              this.map,
              latlngs[0],
              latlngs[1]
            ) < 60
          ) {
            continue
          }
          const angle = CanvasShipUtils.getAngleByLatLng(
            previousLatLng,
            currentLatLng
          )

          if (Math.abs(angle) > 90) {
            latlngs.reverse()
          }
          const cogText = `CA ${this.items[i].cog}`
          const textOptions = {
            repeat: false,
            offset: -8,
            center: true,
            attributes: {
              fill: '#666',
              'font-weight': '100',
              'font-size': '12px',
              'font-family': 'Microsoft YaHei',
              stroke: lineTextColor,
              'stroke-width': '0.5px',
            },
          }
          const distanceText = this.items[i].pDistance + unit
          const polyline = L.polyline(latlngs, {
            fillColor: '#fff',
            fillOpacity: 0.5,
            color: '#000000',
            weight: 0,
            opacity: 0,
            lineCap: 'butt',
            lineJoin: 'bevel',
          }) as any
          polyline.setText(cogText, {
            ...textOptions,
            offset: -5,
          })
          polyline.addTo(this.featureGroup)
          this.txtMarkers.push(polyline)
          const distancePolyline = L.polyline(latlngs, {
            fillColor: '#fff',
            fillOpacity: 0.5,
            color: '#000000',
            weight: 0,
            opacity: 0,
            lineCap: 'butt',
            lineJoin: 'bevel',
          }) as any
          distancePolyline.setText(distanceText, {
            ...textOptions,
            offset: 15,
          })
          distancePolyline.addTo(this.featureGroup)
          this.txtMarkers.push(distancePolyline)
        }
      }
    }
  }

  public endDrawTrack(): void {
    const map = this.map
    map.shipsService && map.shipsService.restoreZIndex()
    map.off('click', this._onClick, this)
    map.off('dblclick', this._onDblclick, this)
    map._container.classList.remove('m_cursor')
    map.doubleClickZoom.enable()
    if (this.options.drawEndCallBack) {
      let llx = 0
      const itemsToProcess = this.items.map((item) => {
        const adjustedItem = Object.assign({}, item)
        if (adjustedItem.lng >= 180 && llx !== 360) {
          llx = 360
        }
        if (map.offsetC) {
          const adjustedLatlng = CanvasShipUtils.getGPSLatLngByMap(
            L.latLng(adjustedItem.lat, adjustedItem.lng),
            map
          )
          adjustedItem.lat = adjustedLatlng.lat
          adjustedItem.lng = adjustedLatlng.lng
        }
        return adjustedItem
      })
      this.options.drawEndCallBack(
        CanvasShipUtils.latlngsTo180(itemsToProcess),
        llx
      )
    }
  }

  private _drawLine(): void {
    const latLngs = this._getMarkerLatLngs()
    if (latLngs && latLngs.length > 0) {
      if (!this.featureGroup) {
        this.featureGroup = L.featureGroup().addTo(this.map)
      }
      const lineColor = this.options.lineColor || '#DA462D'
      const polylineOptions = {
        renderer: this.map.canvas_track,
        color: lineColor,
      } as any
      if (this.options.dash) {
        polylineOptions.dash = this.options.dash
      }
      if (this.options.dashArray && this.options.dashArray.length === 2) {
        polylineOptions.dashArray = this.options.dashArray
      }
      if (this.polyLineLayer) {
        this.polyLineLayer.setLatLngs(latLngs)
      } else {
        this.polyLineLayer = L.polyline(latLngs, polylineOptions).addTo(
          this.featureGroup
        )
      }
    }
  }

  public editLine(): void {
    this.endDrawTrack()
    this._removeAllMarker()
    this._removeAllLine()

    const lineColor = this.options.lineColor || '#DA462D'
    const options = L.extend(
      {
        hintlineStyle: {
          color: lineColor,
          dash: this.options.dash,
          dashArray: this.options.dashArray,
          weight: 0,
        },
        templineStyle: {
          color: lineColor,
          dash: this.options.dash,
          dashArray: this.options.dashArray,
        },
        pathOptions: {
          fill: false,
          color: lineColor,
          dash: this.options.dash,
          dashArray: this.options.dashArray,
        },
        snappable: true,
        snapDistance: 15,
      },
      this.options,
      {
        shape: 'Line',
      }
    )

    const latLngs = this._getLatLngs()
    if (latLngs && latLngs.length > 0) {
      if (this.map.shipsService) {
        this.map.shipsService.setZIndex()
      }
      const draw = drawUtil(toRaw(unref(this.map)))
      this.polyLineLayer = draw._showPolyLine(latLngs, options)
      draw.edit(this.polyLineLayer)
      this.polyLineLayer.on('pm:edit', () => {
        this.setItemsByArray(this.polyLineLayer.getLatLngs())
        this._drawText()
      })
      this._drawText()
    }
  }

  public cancelLine() {
    const draw = drawUtil(toRaw(unref(this.map)))
    draw.cancelEdit(this.polyLineLayer)
  }

  private _removeAllMarker(): void {
    const map = this.map
    for (let i = 0; i < this.markers.length; i++) {
      if (map.markersLayer_track) {
        map.markersLayer_track.removeMarker(this.markers[i])
      }
      this.markers[i].closeTooltip()
      this.markers[i].remove()
    }
    if (map.markersLayer_track) {
      map.markersLayer_track._reset()
    }
    this.markers = []
  }

  private _removeAllTxtMarker(): void {
    for (let i = 0; i < this.txtMarkers.length; i++) {
      this.txtMarkers[i].remove()
    }
    this.txtMarkers = []
  }

  private _removeAllLine(): void {
    if (this.polyLineLayer) {
      this.polyLineLayer.remove()
      this.polyLineLayer = null
    }
  }

  private _drawAllMarker(): void {
    this._removeAllMarker()
    const map = toRaw(unref(this.map))
    if (this.items) {
      if (!this.featureGroup) {
        this.featureGroup = L.featureGroup().addTo(map)
      }
      const bounds = map.getBounds()
      let index = 0
      const len = this.items.length
      for (let i = 0; i < len; i++) {
        const item = this.items[i]
        const marker = this._addOneMarker(i)
        let showText = false
        if (bounds.contains(marker.getLatLng())) {
          if (i === len - 1 || i === index) {
            showText = this._markerTextShow(true, true, marker, item.name)
            index = i
          } else {
            showText = this._markerTextShow(
              true,
              false,
              marker,
              item.name,
              this.markers[index]
            )
            if (showText) {
              index = i
            }
          }

          if (map.markersLayer_track) {
            map.markersLayer_track.addMarker(marker)
          }

          if (showText) {
            marker.openTooltip()
          }
        } else {
          index = i + 1
        }
      }
    }
  }
}

export const trackDrawSymbolImpl = (
  map: any,
  options?: TrackDrawSymbolImplOptions,
  trackData?: any
) => new TrackDrawSymbolImpl(map, options, trackData)
